<script setup>
import {ref, onBeforeUnmount, computed} from "vue";
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline';

const model = defineModel();
const {getFileType, toBase64, uploadToUrl} = useCrudConverters();

const props = defineProps({
  disableUploads: {
    type: Boolean,
    required: false
  }
});

const editor = useEditor({
  content: model.value,
  extensions: [TiptapStarterKit,Underline, Image, Link.configure({
    openOnClick: false,
    autolink: true,
    defaultProtocol: 'https',
  }),],
  onUpdate: (event) => {
    model.value = event.editor.getHTML();
  },
  onCreate: (editor) => {

  }
});

onBeforeUnmount(() => {
  unref(editor).destroy();
});

const tools = computed(() => {
  if (!editor.value) return [];

  const baseTools = [
    {
      icon: 'mdi-format-bold',
      onClick: () => editor.value.chain().focus().toggleBold().run(),
      isActive: () => editor.value.isActive('bold'),
    },
    {
      icon: 'mdi-format-italic',
      onClick: () => editor.value.chain().focus().toggleItalic().run(),
      isActive: () => editor.value.isActive('italic'),
    },
    {
      icon: 'mdi-format-underline',
      onClick: () => editor.value.chain().focus().toggleUnderline().run(),
      isActive: () => editor.value.isActive('underline'),
    },
    {
      icon: 'mdi-format-strikethrough',
      onClick: () => editor.value.chain().focus().toggleStrike().run(),
      isActive: () => editor.value.isActive('strike'),
    },
    {
      icon: 'mdi-format-paragraph',
      onClick: () => editor.value.chain().focus().setParagraph().run(),
      isActive: () => editor.value.isActive('paragraph'),
    },
    {
      icon: 'mdi-format-header-1',
      onClick: () => editor.value.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: () => editor.value.isActive('heading', { level: 1 }),
    },
    {
      icon: 'mdi-format-header-2',
      onClick: () => editor.value.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: () => editor.value.isActive('heading', { level: 2 }),
    },
    {
      icon: 'mdi-format-header-3',
      onClick: () => editor.value.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: () => editor.value.isActive('heading', { level: 3 }),
    },
    {
      icon: 'mdi-format-header-4',
      onClick: () => editor.value.chain().focus().toggleHeading({ level: 4 }).run(),
      isActive: () => editor.value.isActive('heading', { level: 4 }),
    },
    {
      icon: 'mdi-format-list-bulleted',
      onClick: () => editor.value.chain().focus().toggleBulletList().run(),
      isActive: () => editor.value.isActive('bulletList'),
    },
    {
      icon: 'mdi-format-list-numbered',
      onClick: () => editor.value.chain().focus().toggleOrderedList().run(),
      isActive: () => editor.value.isActive('orderedList'),
    },
    {
      icon: 'mdi-code-tags',
      onClick: () => editor.value.chain().focus().toggleCodeBlock().run(),
      isActive: () => editor.value.isActive('codeBlock'),
    },
    {
      icon: 'mdi-format-quote-open',
      onClick: () => editor.value.chain().focus().toggleBlockquote().run(),
      isActive: () => editor.value.isActive('blockquote'),
    },
    {
      icon: 'mdi-minus',
      onClick: () => editor.value.chain().focus().setHorizontalRule().run(),
      isActive: () => false,
    },
    {
      icon: 'mdi-undo',
      onClick: () => editor.value.chain().focus().undo().run(),
      isActive: () => editor.value.isActive('undo'),
    },
    {
      icon: 'mdi-redo',
      onClick: () => editor.value.chain().focus().redo().run(),
      isActive: () => editor.value.isActive('redo'),
    },
    {
      icon: 'mdi-link',
      onClick: () => {
        const previousUrl = editor.value.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);

        if (url === null) return;

        if (url === '') {
          editor.value.chain().focus().extendMarkRange('link').unsetLink().run();
          return;
        }

        editor.value.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
      },
      isActive: () => editor.value.isActive('link'),
    }
  ];

  if (!props.disableUploads) {
    baseTools.push({
      icon: 'mdi-image',
      onClick: () => dialog.value = true,
      isActive: () => dialog.value,
    });
  }

  return baseTools;
});

const config = useRuntimeConfig();
const dialog = ref(false);
const filesToUpload = ref([]);
const searchValue = ref('');
const {
  createItem: createUpload,
  formErrors: imageErrors,
  formPending: uploadsLoading,
  currentPage: uploadsPage
} = useCrudApi("uploads");

const processUploads = async () => {
  uploadsLoading.value = true;
  for (let file of filesToUpload.value) {
    let upload = {
      base64Data: await toBase64(file),
      uploadType: getFileType(file),
      isPublic: true,
      originalFilename: file.name,
    };
    await createUpload(upload);
  }
  uploadsLoading.value = false;
};

const onSelectImage = (item) => {
  editor.value.chain().focus().setImage({src: item}).run();
  dialog.value = false;
};

</script>
<template>
  <div class="main-writing">
    <div class="overflow-x-auto" v-if="editor" style="border:1px solid rgb(var(--v-theme-borderLight)); border-radius:10px;">
      <v-btn color="textSubtle" variant="text" :icon="true" size="small" class="rounded-0" v-for="tool in tools" :active="tool.isActive()" @click="tool.onClick()"
             :class="tool.isActive ? { 'is-active': tool.isActive() } : {}">
        <v-icon size="20" :icon="tool.icon"/>
      </v-btn>
    </div>
    <TiptapEditorContent class="editor rounded mt-4 written-content" :editor="editor" style="border:1px solid rgb(var(--v-theme-borderLight));"/>
  </div>
  <v-dialog v-model="dialog" max-width="1500">
      <v-card class="tl-card flex-fill flex-col" :elevation="0">
        <h2 class="mb-0 pa-4" style="border-bottom:1px solid rgb(var(--v-theme-borderLight))">Gallery</h2>
        <div style="height: 90vh; overflow: auto;" class="pa-4 d-flex flex-column">
          <div class="pa-3 rounded mb-3" style="border:1px solid rgb(var(--v-theme-borderLight))">
            <h4 class="mb-4">Upload new media</h4>
            <div class="d-flex">
              <div class="flex-fill">
                <v-file-input
                    :multiple="false"
                    :label="'Select image to upload'"
                    v-model="filesToUpload"
                    :disabled="uploadsLoading"
                />
                <crud-error-display :errors="imageErrors"></crud-error-display>
              </div>
              <v-btn :loading="uploadsLoading"
                     class="ml-2"
                     @click="processUploads"
                     color='primary'
                     :disabled="filesToUpload === null || filesToUpload.length === 0">
                <v-icon size="small" class="mr-1">mdi-upload</v-icon>
                Upload
              </v-btn>
            </div>
          </div>

          <div class="pa-3 rounded" style="border:1px solid rgb(var(--v-theme-borderLight))">
            <v-text-field
                v-model="searchValue"
                label="Search by file name"
                :disabled="uploadsLoading"/>
            <crud-list-loader :custom-filters="{keywords: searchValue}"
                              :debounced="true"
                              :per-page="60"
                              :page="uploadsPage"
                              path="api/v2/uploads">
              <template #empty>
                <v-alert>
                  No media for filter, upload new instead
                </v-alert>
              </template>

              <template #default="{items,currentPage,totalPages}">
                <v-row justify="start">
                  <v-col v-for="item in items" :key="item.id" class="d-flex flex-column flex-wrap">
                    <v-menu offset-y>
                      <template v-slot:activator="{ props }">
                        <crud-upload-field-selection
                            v-bind="props"
                            :item="item"
                            v-model="model"
                        >
                        </crud-upload-field-selection>
                      </template>
                      <v-list>
                        <v-list-item @click="onSelectImage(uploadToUrl(item,'thumbnail'))">Thumbnail</v-list-item>
                        <v-list-item @click="onSelectImage(uploadToUrl(item,'medium'))">Medium</v-list-item>
                        <v-list-item @click="onSelectImage(uploadToUrl(item,'large'))">Large</v-list-item>
                        <v-list-item @click="onSelectImage(uploadToUrl(item,'original'))">Original</v-list-item>
                      </v-list>
                    </v-menu>

                  </v-col>
                  <v-col cols="12">
                    <v-pagination :length="totalPages" v-model="uploadsPage"/>
                  </v-col>
                </v-row>
              </template>
            </crud-list-loader>

          </div>
        </div>
        <div class="pa-4" style="border-top:1px solid rgb(var(--v-theme-borderLight))">
          <v-btn :loading="uploadsLoading" @click="dialog = false" variant="text" color="textSubtle">
            <v-icon size="small" class="mr-1">mdi-close</v-icon>
            Cancel
          </v-btn>
        </div>
      </v-card>
  </v-dialog>
</template>

<style lang="scss">
.tiptap {
  padding: 20px;

  img {
    display: block;
    height: auto;
    margin: 1.5rem 0;
    max-width: 100%;
  }
}
</style>
